import LegalDocument from '@/components/LegalDocument'
import { useUser } from '@/hooks/useUser'
import { useUpdateUserMutation } from '@/store/api/userApi'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const AcceptTermsModal = () => {
  const [open, setOpen] = useState(false)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const { data: user } = useUser()
  const { t } = useTranslation()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const contentRef = useRef<HTMLDivElement>(null)

  const [updateUser, { isLoading }] = useUpdateUserMutation()

  useEffect(() => {
    if (user && !user.hasAcceptedCustomerPrivacyAndTerms) {
      setOpen(true)
    }
  }, [user?.hasAcceptedCustomerPrivacyAndTerms])

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement
    const scrolledToBottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + 10
    if (scrolledToBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true)
    }
  }

  const onAccept = async () => {
    if (!user) return

    await updateUser({
      ...user,
      hasAcceptedCustomerPrivacyAndTerms: true,
    })

    setOpen(false)
  }

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(2px)',
          },
        },
      }}
      maxWidth="md"
      fullScreen={fullScreen}
    >
      <DialogContent
        ref={contentRef}
        onScroll={handleScroll}
        sx={{ bgcolor: 'background.paper' }}
      >
        <DialogContentText>
          <LegalDocument type="termsAndConditions" popup />
          <Divider sx={{ my: 2 }} />
          <LegalDocument type="privacyPolicy" popup />
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          fullWidth
          variant="contained"
          onClick={onAccept}
          disabled={isLoading || !hasScrolledToBottom}
        >
          {t('accept-terms-button')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
